import { Box, Grid } from "@mui/system";
import OverviewAccountSummaryTable from "./OverviewAccountSummaryTable";
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import React from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  sitePerformanceAll,
  sitePerformanceIncome,
  sitePerformancePerdformance,
  sitePerformanceUserBehavior,
} from "../../../../../AssetManagement/Overview/BusinessLeaderDashboard/AdditionalQueries/AdditionalQueriesAccordion/AdditionalQueriesConstants";
import {
  selectBizOverviewAccountSummaryLoadedRequestKey,
  selectBizOverviewAccountSummaryLoading,
  selectBizOverviewAccountSummaryOutput,
} from "../../../../../../features/bizDevelopmentDashboardSlice";
import {
  buildOverviewAccountSummaryRequestKey,
  OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES,
  OverviewAccountSummaryTimeframe,
} from "../../../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardEndPointLogic";
import { useBizDevelopmentDashboard } from "../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";
import { exportOverviewAccountSummaryToCSV } from "./overviewAccountSummaryTableHelpers";

interface TimeframeOption {
  value: OverviewAccountSummaryTimeframe;
  label: string;
}

const DATA_SET_COLUMNS = {
  all: sitePerformanceAll,
  "user-behavior": sitePerformanceUserBehavior,
  income: sitePerformanceIncome,
  performance: sitePerformancePerdformance,
};

type DataSetValue = keyof typeof DATA_SET_COLUMNS;

export interface OverviewAccountSummaryRef {
  refresh: () => void;
}

const timeFrame: TimeframeOption[] = [
  {
    value: OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES.lastThirtyDays,
    label: "Last 30 Days",
  },
  {
    value: OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES.sixWeeks,
    label: "Last 6 Weeks",
  },
  {
    value: OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES.lastTwelveMonthsFull,
    label: "Last 12 Months Full",
  },
  {
    value: OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES.lastTwelveMonthsProportional,
    label: "Last 12 Months Proportional",
  },
];

const OverviewAccountSummary = React.forwardRef<OverviewAccountSummaryRef>(
  (_, ref) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedDataSet, setSelectedDataSet] =
      React.useState<DataSetValue>("all");
    const isLoading = useSelector(selectBizOverviewAccountSummaryLoading);
    const rows = useSelector(selectBizOverviewAccountSummaryOutput);
    const loadedRequestKey = useSelector(
      selectBizOverviewAccountSummaryLoadedRequestKey,
    );
    const { fetchOverviewAccountSummaryData, getSitesFromParams } =
      useBizDevelopmentDashboard();
    const selectedTimeframe =
      timeFrame.find(
        (option) =>
          option.value === searchParams.get("timeframeOverviewAccountSummary"),
      ) ?? timeFrame[0];
    const id = React.useId();

    React.useEffect(() => {
      const sites = getSitesFromParams();
      const requestKey = buildOverviewAccountSummaryRequestKey(
        selectedTimeframe.value,
        sites,
      );
      if (loadedRequestKey === requestKey) return;

      fetchOverviewAccountSummaryData(selectedTimeframe.value, sites);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTimeframeChange = (_: any, newValue: TimeframeOption | null) => {
      if (!newValue) return;

      setSearchParams((prev) => {
        prev.set("timeframeOverviewAccountSummary", newValue.value);
        prev.set("pageOverviewAccountSummary", "0");
        return prev;
      });
      fetchOverviewAccountSummaryData(newValue.value, getSitesFromParams());
    };

    const handleDataSetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedDataSet(event.target.value as DataSetValue);
      setSearchParams((prev) => {
        prev.set("pageOverviewAccountSummary", "0");
        return prev;
      });
    };

    const handleDownload = () => {
      exportOverviewAccountSummaryToCSV({
        columns: DATA_SET_COLUMNS[selectedDataSet],
        rows: rows ?? [],
        filename: `overview_account_summary_${selectedTimeframe.value}_${selectedDataSet}`,
      });
    };

    React.useImperativeHandle(ref, () => ({
      refresh: () => {
        fetchOverviewAccountSummaryData(
          selectedTimeframe.value,
          getSitesFromParams(),
        );
      },
    }));

    return (
    <>
      <Grid container spacing={2} alignItems="center" px={2} py={1.5}>
        <Grid size={8}>
          <FormControl disabled={isLoading}>
            <FormLabel id={`${id}-label`} color="secondary">
              Data set:
            </FormLabel>
            <RadioGroup
              value={selectedDataSet}
              onChange={handleDataSetChange}
              row
              aria-labelledby={`${id}-label`}
              name="row-radio-buttons-group"
            >
              <FormControlLabel
                data-tracker="838"
                value="all"
                control={<Radio color="secondary" />}
                label="All"
              />
              <FormControlLabel
                data-tracker="839"
                value="user-behavior"
                control={<Radio color="secondary" />}
                label="User Behavior"
              />
              <FormControlLabel
                data-tracker="840"
                value="income"
                control={<Radio color="secondary" />}
                label="Income"
              />
              <FormControlLabel
                data-tracker="841"
                value="performance"
                control={<Radio color="secondary" />}
                label="Performance"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid size={4}>
          <Box display={"flex"} alignItems={"end"} justifyContent={"flex-end"}>
            <Autocomplete
              id="assetOrSiteName"
              disabled={isLoading}
              disablePortal
              options={timeFrame}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              value={selectedTimeframe}
              onChange={handleTimeframeChange}
              size="small"
              sx={{ pt: "8px", width: "275px" }}
              renderInput={(params) => (
                <TextField
                  color="secondary"
                  {...params}
                  required
                  placeholder="Search Time Frame or choose from the list"
                  label="Time Frame"
                  data-tracker="835"
                />
              )}
            />
            <IconButton
              aria-label="download"
              color="secondary"
              sx={{ ml: 2 }}
              disabled={isLoading}
              onClick={handleDownload}
              data-tracker="836"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      <OverviewAccountSummaryTable
        tableHeadings={DATA_SET_COLUMNS[selectedDataSet]}
        rows={rows ?? []}
        selectedTimeframeLabel={selectedTimeframe.label}
      />
    </>
    );
  },
);

export default OverviewAccountSummary;
